import Icon from '@/components/icons/Icon';
import type { ReactHookFormRegister } from '@/components/modules/checkout/CheckoutFormFields/CheckoutFormFields';
import { classnames } from '@/helpers';
import { InputHTMLAttributes, forwardRef, useRef, useState } from 'react';
import { UseFormSetValue } from 'react-hook-form';
import { twMerge } from 'tailwind-merge';

type GenericInputFieldProps = {
  id: string;
  label?: string;
  error?: string;
  leftIcon?: React.ReactNode;
  rightIcon?: React.ReactNode;
  leftPadding?: boolean;
  rightPadding?: boolean;
  setValue?: UseFormSetValue<any>;
  handleClear?: () => void;
  placeholder?: string;
};

type InputFieldProps = InputHTMLAttributes<HTMLInputElement> & GenericInputFieldProps & ReactHookFormRegister<any>;

function assignRef<T>(ref: React.ForwardedRef<T>, value: T | null) {
  if (typeof ref === 'function') ref(value);
  else if (ref) (ref as React.MutableRefObject<T | null>).current = value;
}

const InputField = forwardRef<HTMLInputElement, InputFieldProps>(
  (
    {
      id,
      label,
      error,
      leftIcon,
      rightIcon,
      className,
      disabled,
      leftPadding = true,
      rightPadding = true,
      setValue,
      handleClear,
      onBlur: onBlurFromProps,
      onFocus: onFocusFromProps,
      placeholder,
      ...props
    },
    ref,
  ) => {
    const [isFocused, setIsFocused] = useState(false);
    const localInputRef = useRef<HTMLInputElement>(null);
    const inputValue = props.value;
    const showClearButton = handleClear && isFocused && inputValue !== undefined && inputValue !== '' && !disabled;

    const handleOnFocus = (event: React.FocusEvent<HTMLInputElement>) => {
      setIsFocused(true);
      if (onFocusFromProps) {
        onFocusFromProps(event);
      }
    };

    const handleOnBlur = (event: React.FocusEvent<HTMLInputElement>) => {
      setIsFocused(false);
      if (onBlurFromProps) {
        onBlurFromProps(event);
      }
    };

    const handleOnClear = () => {
      if (setValue) {
        setValue(id, '');
        setIsFocused(true);
      }
      if (handleClear) {
        handleClear();
      }
    };

    const handleClick = () => {
      if (localInputRef.current) {
        localInputRef.current.focus();
      }
    };

    return (
      <div
        onClick={handleClick}
        className={classnames(
          leftPadding && 'pl-lg',
          rightPadding && 'pr-lg',
          'flex w-full flex-col',
          disabled && 'opacity-40',
        )}>
        <div
          className={classnames(
            'px-sm gap-xs py-xs bg-bg_neutral relative flex min-h-[48px] items-center rounded-sm border',
            error ? 'border-border_negative' : 'border-border_regular',
            'float-group',
          )}>
          {leftIcon && <div className="w-7">{leftIcon}</div>}
          <div className="relative h-full w-full">
            {label && (
              <label
                htmlFor={id}
                className={classnames(
                  'text-fg_secondary text-l absolute left-0 top-[5px] mb-0 block',
                  disabled && 'text-fg_disabled',
                  'z-10',
                )}>
                {label}
              </label>
            )}
            <input
              id={id}
              ref={(node) => {
                localInputRef.current = node;
                assignRef(ref, node); // merge refs
              }}
              type={'text'}
              className={twMerge(
                'text-fg_primary text-l placeholder:text-s peer w-full !pt-0 outline-none disabled:bg-transparent',
                className,
                (isFocused || inputValue !== '') && 'translate-y-xs !mt-0 h-full',
                (!isFocused || inputValue) && 'placeholder-transparent',
                !isFocused && !inputValue && '!mt-xs',
              )}
              disabled={disabled}
              placeholder={isFocused && inputValue === '' ? placeholder : ''}
              onFocus={handleOnFocus}
              onBlur={handleOnBlur}
              {...props}
            />
          </div>
          {showClearButton && (
            <Icon
              className="clear-button cursor-pointer"
              variant="close-circle"
              color="fg_secondary"
              onMouseDown={(e) => e.preventDefault()}
              onClick={handleOnClear}
            />
          )}
          {rightIcon}
        </div>
        {error && <div className="text-fg_negative text-xxs mt-xxs flex justify-start">{error}</div>}
      </div>
    );
  },
);

export default InputField;
